import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faCertificate, faPercent, faClock, faCheck, faAward } from '@fortawesome/free-solid-svg-icons';
import SectionTitle from '../UI/SectionTitle';
import FeatureCard from '../UI/FeatureCard';

// Импортируем видео для секции "О нас"
import aboutVideo from '../../assets/videos/about-video.mp4';

// Запасное изображение для мобильных устройств
const fallbackImage = 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1500&q=80';

const AboutSection = styled.section`
  padding: var(--spacing-xxl) 0;
  background-color: var(--color-background-alt);
`;

const AboutContainer = styled.div`
  max-width: var(--container-width);
  padding: 0 var(--container-padding);
  margin: 0 auto;
`;

const VideoSection = styled.div`
  position: relative;
  height: 80vh;
  min-height: 600px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-xxl);
  
  @media (max-width: 992px) {
    height: 70vh;
  }
  
  @media (max-width: 768px) {
    height: 600px;
  }
`;

const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background-image: url(${props => props.fallbackImage});
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.8) 100%
    );
    z-index: 2;
  }
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    z-index: 1;
  }
`;

const TextOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 80px;
  z-index: 3;
  color: white;
  
  @media (max-width: 992px) {
    padding: 50px;
  }
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const AboutTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
  
  span {
    color: var(--color-secondary);
  }
  
  @media (max-width: 992px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AboutSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  max-width: 700px;
  margin-bottom: 30px;
  opacity: 0.9;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Highlights = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const HighlightItem = styled.div`
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(5px);
  padding: 15px 25px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
    color: var(--color-secondary);
  }
  
  span {
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    font-size: 0.9rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const CounterSection = styled.div`
  background-color: var(--color-primary);
  padding: var(--spacing-xl) 0;
  margin-top: var(--spacing-xxl);
  color: white;
`;

const CounterContainer = styled.div`
  max-width: var(--container-width);
  padding: 0 var(--container-padding);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const CounterItem = styled(motion.div)`
  text-align: center;
`;

const CounterNumber = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-xs);
  color: white;
`;

const CounterText = styled.p`
  font-size: 1.1rem;
  margin: 0;
  opacity: 0.9;
`;

const About = () => {
  const features = [
    {
      icon: faTruck,
      title: 'Доставка 24/7',
      description: 'Быстрая доставка по всей стране в любое удобное для вас время.'
    },
    {
      icon: faCertificate,
      title: 'Сертифицированная продукция',
      description: 'Вся наша продукция имеет необходимые сертификаты качества.'
    },
    {
      icon: faPercent,
      title: 'Скидки на опт',
      description: 'Выгодные условия для оптовых заказчиков и постоянных клиентов.'
    },
    {
      icon: faClock,
      title: 'Оперативное исполнение',
      description: 'Обработка заказа и отгрузка в кратчайшие сроки.'
    },
    {
      icon: faCheck,
      title: 'Гарантированное качество',
      description: 'Контроль качества на всех этапах производства и доставки.'
    },
    {
      icon: faAward,
      title: 'Опыт более 7 лет',
      description: 'Многолетний опыт работы и тысячи довольных клиентов.'
    }
  ];
  
  const highlights = [
    { icon: faCheck, text: 'Собственное производство' },
    { icon: faCheck, text: 'Индивидуальные решения' },
    { icon: faCheck, text: 'Быстрая доставка' },
    { icon: faCheck, text: 'Консультации экспертов' }
  ];
  
  return (
    <>
      <AboutSection id="about">
        <AboutContainer>          
          <VideoSection>
            <VideoBackground fallbackImage={fallbackImage}>
              <video autoPlay muted loop playsInline>
                <source src={aboutVideo} type="video/mp4" />
              </video>
            </VideoBackground>
            
            <TextOverlay>
              <AboutTitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                О компании <span>Купить-Стрейч.РФ</span>
              </AboutTitle>
              
              <AboutSubtitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Мы создаем решения, которые обеспечивают безопасность и целостность вашей продукции на всех этапах логистической цепочки.
              </AboutSubtitle>
              
              <Highlights
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {highlights.map((item, index) => (
                  <HighlightItem key={index}>
                    <FontAwesomeIcon icon={item.icon} />
                    <span>{item.text}</span>
                  </HighlightItem>
                ))}
              </Highlights>
            </TextOverlay>
          </VideoSection>
          
          <SectionTitle 
            title="Почему выбирают нас"
            subtitle="Мы стремимся предоставить лучший сервис и продукцию высочайшего качества"
          />
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={0.1 * index}
              />
            ))}
          </FeaturesGrid>
        </AboutContainer>
      </AboutSection>
      
      <CounterSection>
        <CounterContainer>
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <CounterNumber>7+</CounterNumber>
            <CounterText>лет на рынке</CounterText>
          </CounterItem>
          
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <CounterNumber>500+</CounterNumber>
            <CounterText>довольных клиентов</CounterText>
          </CounterItem>
          
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <CounterNumber>1000+</CounterNumber>
            <CounterText>успешных доставок</CounterText>
          </CounterItem>
          
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <CounterNumber>24/7</CounterNumber>
            <CounterText>поддержка клиентов</CounterText>
          </CounterItem>
        </CounterContainer>
      </CounterSection>
    </>
  );
};

export default About; 